import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { Bell, Globe, Menu, LogOut, LayoutDashboard, MessageCircle, UserCircle2 } from "lucide-react";
import { InstallButton } from "@/components/InstallPrompt";
import { useConfig, supportWa } from "@/lib/config";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useI18n, LANGS } from "@/lib/i18n";
import { useAuth, homeFor } from "@/lib/auth";
import { api, fmtDate } from "@/lib/api";
import { trNotif } from "@/lib/notifTr";

const LINKS = [["/", "nav.home"], ["/services", "nav.services"], ["/cleaning", "cl.nav"], ["/build", "b.nav"], ["/how-it-works", "nav.how"], ["/join", "nav.join"]];

function LangSwitcher({ compact, testId = "language-switcher" }) {
  const { lang, setLang } = useI18n();
  return (
    <Select value={lang} onValueChange={setLang}>
      <SelectTrigger className={`${compact ? "w-[78px] px-2" : "w-[130px]"} h-9 bg-white/70`} data-testid={testId} aria-label="Language">
        <Globe className="h-4 w-4 me-1 text-blue-700 shrink-0" />
        {compact ? <span className="text-xs font-semibold uppercase">{lang}</span> : <SelectValue />}
      </SelectTrigger>
      <SelectContent>
        {LANGS.map((l) => <SelectItem key={l.code} value={l.code} data-testid={`lang-option-${l.code}`}>{l.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function NotificationBell() {
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [data, setData] = useState({ items: [], unread: 0 });
  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, []);
  const readAll = async () => { await api.post("/notifications/read-all"); load(); };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="relative p-2 rounded-full hover:bg-slate-100 transition-colors" data-testid="notification-bell">
          <Bell className="h-5 w-5 text-slate-700" />
          {data.unread > 0 && <span data-testid="notification-unread-count" className="absolute -top-0.5 -end-0.5 bg-amber-500 text-slate-900 text-[10px] font-bold rounded-full h-5 min-w-5 px-1 flex items-center justify-center">{data.unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <span className="font-semibold text-sm">{t("d.notifications")}</span>
          <button onClick={readAll} className="text-xs text-teal-700 hover:underline" data-testid="notification-mark-read">{t("d.mark_read")}</button>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {data.items.length === 0 && <p className="p-6 text-sm text-slate-500 text-center">{t("d.no_notifs")}</p>}
          {data.items.map((n) => (
            <button key={n.id} onClick={() => n.link && nav(n.link)} data-testid="notification-item"
              className={`w-full text-start px-4 py-3 border-b last:border-0 hover:bg-slate-50 ${n.read ? "" : "bg-teal-50/60"}`}>
              <p className="text-sm font-medium text-slate-900">{trNotif(n.title, lang, t)}</p>
              {n.body && <p className="text-xs text-slate-500 line-clamp-2">{trNotif(n.body, lang, t)}</p>}
              <p className="text-[10px] text-slate-400 mt-1 font-mono">{fmtDate(n.created_at)}</p>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function NavItems({ onClick, vertical }) {
  const { t } = useI18n();
  return LINKS.map(([to, k]) => (
    <NavLink key={to} to={to} end onClick={onClick} data-testid={`nav-link-${k.split(".")[1]}`}
      className={({ isActive }) => `text-sm font-medium whitespace-nowrap transition-colors ${vertical ? "py-2" : ""} ${isActive ? "text-teal-700" : "text-slate-600 hover:text-slate-900"}`}>
      {t(k)}
    </NavLink>
  ));
}

function Header() {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const out = () => { logout(); nav("/"); };
  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-[#F8FAFC]/80 border-b border-slate-200">
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 sm:h-16 flex items-center gap-4">
        <div className="sm:hidden"><LangSwitcher compact testId="mobile-language-switcher" /></div>
        <Link to="/" className="absolute left-1/2 -translate-x-1/2 sm:static sm:translate-x-0 flex items-center gap-2 shrink-0" data-testid="logo-link">
          <img src="/img/logo.png" alt="NicaFix Nicaragua logo" className="h-16 sm:h-12 w-auto" data-testid="header-logo" />
          <span className="hidden sm:inline text-xl font-extrabold tracking-tight text-[#0B2A5B]">NicaFix</span>
        </Link>
        <nav className="hidden xl:flex items-center gap-5 ms-6"><NavItems /></nav>
        <div className="ms-auto flex items-center gap-2">
          <div className="hidden sm:block"><LangSwitcher /></div>
          {user && <NotificationBell />}
          {user ? (
            <>
              <Button variant="outline" size="sm" className="hidden sm:flex" onClick={() => nav(homeFor(user.role))} data-testid="nav-dashboard-btn"><LayoutDashboard className="h-4 w-4 me-1" />{t("nav.dashboard")}</Button>
              <button onClick={out} className="p-2 rounded-full hover:bg-slate-100" title={t("nav.logout")} data-testid="nav-logout-btn"><LogOut className="h-4 w-4" /></button>
            </>
          ) : (
            <Link to="/login" className="hidden sm:inline text-sm font-medium text-slate-700 hover:text-teal-700 px-2" data-testid="nav-login-link">{t("nav.login")}</Link>
          )}
          <Button size="sm" className="hidden md:flex rounded-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold" onClick={() => nav("/request")} data-testid="nav-request-btn">{t("nav.request")}</Button>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild><button className="xl:hidden p-2" data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></button></SheetTrigger>
            <SheetContent className="flex flex-col gap-3 pt-12">
              <NavItems vertical onClick={() => setOpen(false)} />
              <Link to="/request" onClick={() => setOpen(false)} className="py-2 text-sm font-semibold text-amber-600">{t("nav.request")}</Link>
              {user ? <Link to={homeFor(user.role)} onClick={() => setOpen(false)} className="py-2 text-sm">{t("nav.dashboard")}</Link>
                : <Link to="/login" onClick={() => setOpen(false)} className="py-2 text-sm">{t("nav.login")}</Link>}
              <LangSwitcher testId="menu-language-switcher" />
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}

function Footer() {
  const { t } = useI18n();
  const cols = [["/services", "nav.services"], ["/how-it-works", "nav.how"], ["/join", "nav.join"], ["/request", "nav.request"]];
  return (
    <footer className="bg-slate-950 text-slate-300 pt-16 pb-32 md:pb-24 px-4 md:px-8" data-testid="site-footer">
      <div className="max-w-7xl mx-auto grid md:grid-cols-[2fr_1fr_1fr] gap-10">
        <div>
          <img src="/img/logo.png" alt="NicaFix Nicaragua logo" className="h-24 w-auto" data-testid="footer-logo" />
          <p className="mt-3 text-sm max-w-sm">{t("ft.tag")}</p>
          <InstallButton className="mt-5 rounded-full bg-white/10 hover:bg-white/20 text-white px-4 py-2 text-sm font-semibold transition-colors" testid="footer-install-btn" />
          <p className="mt-6 text-xs text-red-300 max-w-sm">{t("urgent.title")} · 911</p>
        </div>
        <div className="flex flex-col gap-2 text-sm">
          {cols.map(([to, k]) => <Link key={to} to={to} className="hover:text-white transition-colors" data-testid={`footer-link-${k.split(".")[1]}`}>{t(k)}</Link>)}
        </div>
        <div className="flex flex-col gap-2 text-sm">
          <a href="mailto:[contact@nicafix.com]" className="hover:text-white" data-testid="footer-contact">{t("ft.contact")}</a>
          <span data-testid="footer-privacy">{t("ft.privacy")}</span>
          <span data-testid="footer-terms">{t("ft.terms")}</span>
        </div>
      </div>
      <p className="max-w-7xl mx-auto mt-12 text-xs text-slate-500 flex flex-wrap justify-between gap-2">
        <span>© {new Date().getFullYear()} NicaFix. {t("ft.rights")}</span>
        <span data-testid="footer-credit">{t("ft.credit")} <a href="https://www.GregMiceli.com" target="_blank" rel="noopener" className="text-slate-300 hover:text-white underline underline-offset-2" data-testid="footer-credit-link">Greg Miceli</a></span>
      </p>
    </footer>
  );
}

function ScrollToTop() {
  const { pathname, search } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname, search]);
  return null;
}

function ProfileFloat() {
  const { t } = useI18n();
  const { user } = useAuth();
  return createPortal(
    <Link to={user ? homeFor(user.role) : "/login"} style={{ transform: "translateZ(0)" }} data-testid="profile-float-btn"
      className="fixed bottom-4 start-4 sm:bottom-6 sm:start-6 z-50 flex items-center gap-2 rounded-full bg-slate-900 text-white ps-2 pe-4 py-2 shadow-xl hover:bg-blue-700 transition-colors">
      <span className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center text-sm font-bold">{user ? user.name.trim()[0]?.toUpperCase() : <UserCircle2 className="h-5 w-5" />}</span>
      <span className="text-sm font-semibold whitespace-nowrap">{t(user ? "prof.btn" : "prof.login")}</span>
    </Link>,
    document.body
  );
}

function WhatsAppFloat() {
  const { t } = useI18n();
  const cfg = useConfig();
  return createPortal(
    <a href={supportWa(cfg, "Hola NicaFix")} target="_blank" rel="noreferrer" data-testid="whatsapp-float-btn"
      style={{ transform: "translateZ(0)", WebkitTransform: "translateZ(0)" }}
      className="fixed bottom-4 end-4 sm:bottom-6 sm:end-6 z-50 flex items-center gap-2 rounded-full bg-[#25D366] text-white ps-3 pe-4 py-3 shadow-xl hover:bg-[#1ebe5b] transition-colors">
      <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-40 pointer-events-none" />
      <MessageCircle className="relative h-6 w-6" />
      <span className="relative text-sm font-bold whitespace-nowrap">{t("wa.call")}</span>
    </a>,
    document.body
  );
}

export default function Layout() {
  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAFC] overflow-x-clip">
      <ScrollToTop />
      <Header />
      <main className="flex-1"><Outlet /></main>
      <Footer />
      <WhatsAppFloat />
      <ProfileFloat />
    </div>
  );
}
